import { useState, useEffect } from 'react';
import {
    useNavigate,
} from 'react-router-dom';

import { IconPlus, IconShoppingCart, IconKey } from '@tabler/icons-react';

import { useCompanyLicenseHook } from '../../Hooks/Companies/companyLicenseHooks';

import { CompanyDetailsInterface } from '../../Models/Company/Company';
import { CompanyLicenseHistoryList } from '../../Models/Company/CompanyLicenseHistory';

import { TableContainer } from '../../Styles/CustomViews/Pages/ListComponent/customListComponentStyles';

import RouteHelper from '../../Utilities/Helpers/RouteHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';
import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';

import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { CompanyField, CompanyLicenseField } from '../../Utilities/Enums/ParametersEnum';
import { AdminModules } from '../../Utilities/Enums/AdminModulesEnum';
import { ValidityType, ValidityTypeEnum } from '../../Utilities/Enums/ValidityTypeEnums';
import { OrderByEnum } from '../../Utilities/Enums/SortEnums';

import CustomButton from '../../CustomViews/Elements/CustomButton';
import CustomDetailsCard from '../../CustomViews/Pages/CustomDetailsCard/CustomDetailsCard';
import CustomTabs from '../../CustomViews/Elements/CustomTabs/CustomTabs';
import LicenseKeyTitle from '../../CustomViews/Pages/CustomTitles/LicenseKeyTitle';
import CustomProgressBar from '../../CustomViews/Elements/CustomProgressBar/CustomProgressBar';
import CustomListComponent from '../../CustomViews/Pages/ListComponent/CustomListComponent';
import CustomExpirationDate from '../../CustomViews/Elements/CustomExpirationDate';
import LicenseModules from '../Licenses/LicenseModules';

import RenewLicenseModal from './RenewLicenseModal';

import { Card, Typography } from '@mui/material';
import styled from 'styled-components';

const FieldItemsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;

    .moduleContainer {
        grid-column: 1 / span 3;
    }
`;

const FieldItemCard = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
`;

const FieldItemName = styled(Typography) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    color: #878a99 !important;
    font-weight: 600 !important;
    font-size: 0.80rem !important;
    margin: 0px 0px 5px 0px !important;
`;

const FieldItemValue = styled(Typography) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    font-weight: 500 !important;
    font-size: 16px !important;
    word-break: break-all;
`;

const FieldItemValueContainer = styled.div <{ $darkMode?: boolean; }>`
    line-height: 3;

    .MuiTypography-root.MuiTypography-caption.MuiTypography-gutterBottom {
        font-weight: 500 !important;
        font-size: 16px !important;
    }

    button {
        margin: 0px 10px 0px 0px;
    }

    .companyDetailsProgressBar {
        width: 50%;
    }
`;

export default function CompanyLicenses(props: CompanyDetailsInterface) {
    //Variables
    let { companyid, viewType, setviewType, setopenDelete, previewData, getCompanyData } = props;
    let navigate = useNavigate();

    //Use States
    let [selectedTab, setselectedTab] = useState(0);
    let [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        id: companyid,
        sortBy: QueryBuilderHelper.DefaultSortValue(CompanyLicenseField.LICENSE_KEY),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
    });
    let [openRenewLicenseModal, setopenRenewLicenseModal] = useState<boolean>(false);

    let q_queryKey = [CachingEnum.COMPANY_LICENSE_HISTORY, queryStr]
    let { data, fetchStatus } = useCompanyLicenseHook(q_queryKey, queryStr);

    const tableColumns = [
        {
            accessor: CompanyLicenseField.LICENSE_KEY,
            title: 'License Key',
            sortable: true,
            render: (record: CompanyLicenseHistoryList) => record?.[CompanyLicenseField.LICENSE_KEY] ?? "--",
        },
        {
            accessor: CompanyLicenseField.STORAGE_USED,
            title: 'Data Storage',
            sortable: true,
            render: (record: CompanyLicenseHistoryList) => (record?.[CompanyLicenseField.STORAGE_CAPACITY] ?? "0") + " GB",
            // render: (record: CompanyLicenseHistoryList) => <CustomProgressBar className="companyDetailsProgressBar" color="info" valueAmount={record?.[CompanyLicenseField.STORAGE_USED] ?? 0} overAmount={record?.[CompanyLicenseField.STORAGE_CAPACITY] ?? 0} progressTitle={"GB"}/>,
        },
        {
            accessor: CompanyLicenseField.USER_SLOT_USED,
            title: 'No. Of Users',
            sortable: true,
            render: (record: CompanyLicenseHistoryList) => (record?.[CompanyLicenseField.USER_CAPACITY] ?? "0") + " User(s)",
            // render: (record: CompanyLicenseHistoryList) => <CustomProgressBar className="companyDetailsProgressBar" color="warning" valueAmount={record?.[CompanyLicenseField.USER_SLOT_USED] ?? 0} overAmount={record?.[CompanyLicenseField.USER_CAPACITY] ?? 0} progressTitle={"User(s)"}/>,
        },
        {
            accessor: CompanyLicenseField.CREATED_DATE,
            title: 'Purchased/Generated On',
            sortable: true,
            render: (record: CompanyLicenseHistoryList) => record?.[CompanyLicenseField.CREATED_DATE] ? DateHelper.getFormattedDate(record?.[CompanyLicenseField.CREATED_DATE], "dd/MM/yyyy") : "--",
        },
        {
            accessor: CompanyLicenseField.VALIDITY,
            title: 'Validity',
            sortable: true,
            render: (record: CompanyLicenseHistoryList) => (record?.[CompanyLicenseField.VALIDITY] ?? "0") + " " + (ValidityType?.[record?.[CompanyLicenseField.VALIDITY_TYPE]]?.title ?? "Month") + "(s)",
        },
        {
            accessor: CompanyLicenseField.ISSUE_DATE,
            title: 'License Issued Date',
            sortable: true,
            render: (record: CompanyLicenseHistoryList) => record?.[CompanyLicenseField.ISSUE_DATE] ? DateHelper.getFormattedDate(record?.[CompanyLicenseField.ISSUE_DATE], "dd/MM/yyyy") : "--",
        },
        {
            accessor: CompanyLicenseField.EXPIRY_DATE,
            title: 'License Expiry Date',
            sortable: true,
            render: (record: CompanyLicenseHistoryList) => record?.[CompanyLicenseField.EXPIRY_DATE] ? DateHelper.getFormattedDate(record?.[CompanyLicenseField.EXPIRY_DATE], "dd/MM/yyyy") : "--",
        },
        {
            accessor: CompanyLicenseField.ACTION,
            // title: 'License Expiry Date',
            sortable: true,
            // render: (record: CompanyLicenseHistoryList) => record?.[CompanyLicenseField.EXPIRY_DATE] ? DateHelper.getFormattedDate(record?.[CompanyLicenseField.EXPIRY_DATE], "dd/MM/yyyy") : "--",
        },
    ];

    //Rendering Functions
    const renderActionButtons = () => {
        return <>
            <CustomButton
                btnType={ButtonType.PRIMARY}
                title='Add'
                startIcon={<IconPlus />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                	
                }} />
        </>
    }

    const renderLicenseDetails = () => {
        // let _modules = previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.MODULES]?.map((item: any) => { return item.name })?.toString() ?? "";

        return <FieldItemsContainer>
            <FieldItemCard>
                <FieldItemName>License Key</FieldItemName>
                {!previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.LICENSE_KEY] && <FieldItemValue>--</FieldItemValue>}
                {previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.LICENSE_KEY] && <FieldItemValueContainer><LicenseKeyTitle licenseKey={previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.LICENSE_KEY] ?? ""} color="inherit" /></FieldItemValueContainer>}
                
                <FieldItemValueContainer>
                    { AccessibilityHelper.hasAccess(AdminModules.COMPANY_EDIT) && <CustomButton
                        btnType={ButtonType.PRIMARY_LIGHT}
                        title='Renew License'
                        startIcon={<IconKey />}
                        // isRoundedCorner={true}
                        onClick={(e: any) => {
                            setopenRenewLicenseModal(true);
                        }} />}
                    { AccessibilityHelper.hasAccess(AdminModules.BUY_LICENSE) && <CustomButton
                        btnType={ButtonType.PRIMARY_LIGHT}
                        title='Buy License'
                        startIcon={<IconShoppingCart />}
                        // isRoundedCorner={true}
                        onClick={(e: any) => {
                            navigate(`${RouteEnum.BUY_LICENSE}?id=${previewData?.[CompanyField.PUBLIC_ID]}`, { replace: true });
                        }} />}
                </FieldItemValueContainer>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>Data Storage</FieldItemName>
                <FieldItemValueContainer><CustomProgressBar className="companyDetailsProgressBar" color="info" valueAmount={previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.STORAGE_USED] ?? 0} overAmount={previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.STORAGE_CAPACITY] ?? 0} progressTitle={"GB"}/></FieldItemValueContainer>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>Slot Used</FieldItemName>
                <FieldItemValueContainer><CustomProgressBar className="companyDetailsProgressBar" color="warning" valueAmount={previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.USER_SLOT_USED] ?? 0} overAmount={previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.USER_CAPACITY] ?? 0} progressTitle={"User(s)"}/></FieldItemValueContainer>
            </FieldItemCard>

            <FieldItemCard>
                <FieldItemName>Purchased/Generated On</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.ISSUE_DATE] ? DateHelper.getFormattedDate(previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.ISSUE_DATE], "dd/MM/yyyy hh:mm a") : "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>License Issued Date</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.ISSUE_DATE] ? DateHelper.getFormattedDate(previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.ISSUE_DATE], "dd/MM/yyyy") : "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>License Expiry Date</FieldItemName>
                <FieldItemValue>
                    <CustomExpirationDate isFromDetails={true} expiryDate={previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.EXPIRY_DATE] ?? null} />
                </FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>Validity</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.VALIDITY] ?? 0} {(ValidityType?.[previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.VALIDITY_TYPE] ?? ValidityTypeEnum.MONTH]?.title ?? "Month")}(s)</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard className="moduleContainer">
                {/*<FieldItemName>Modules</FieldItemName>*/}
                <FieldItemValueContainer>
                    {renderLicenseModules()}
                </FieldItemValueContainer>
            </FieldItemCard>
        </FieldItemsContainer>


    }

    const renderLicenseHistory = () => {
        return <TableContainer>
                <CustomListComponent
                    fetching={fetchStatus == "fetching"}
                    columns={tableColumns}
                    idAccessor={(record: CompanyLicenseHistoryList) => `companyLicenseHistory${record?.[CompanyLicenseField.PUBLIC_ID]}`}
                    records={data?.data?.list ?? []}
                    totalRecords={data?.data?.metaData?.totalCount ?? 0}
                    page={queryStr.pageNumber}
                    recordsPerPage={queryStr.pageSize}
                    defaultColumnSort={{ columnAccessor: queryStr?.sortBy ?? 'id', direction: queryStr?.orderBy ?? 'asc' }}
                    onSortStatusChange={((sort: any) => { setQueryStr((prev: any) => ({ ...prev, sortBy: sort?.columnAccessor ?? "id", orderBy: sort?.direction ?? "asc", pageNumber: 1 })) })}
                    setPage={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageNumber: p }))}
                    onRecordsPerPageChange={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageSize: p, pageNumber: 1 }))} />
            </TableContainer>
    }

    const renderLicenseModules = () => {
        return <LicenseModules isTableOnly modules={previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.MODULES] ?? []} />
    }

    return <CustomDetailsCard
        isNoPadding={true}
        isTitleNotText={true}
        title={
            <CustomTabs
            selectedTab={selectedTab}
            handleOnChange={(e: any) => {setselectedTab(e)}}
            items={[
                {
                    id: 0,
                    name: "License Details",
                },
                {
                    id: 1,
                    name: "License History",
                },
            ]} />
        } >
            {selectedTab == 0 && renderLicenseDetails()}
            {selectedTab == 1 && renderLicenseHistory()}

            {openRenewLicenseModal && <RenewLicenseModal
                openRenewLicenseModal={openRenewLicenseModal}
                setopenRenewLicenseModal={setopenRenewLicenseModal}
                companyPublicId={companyid}
                getCompanyData={getCompanyData} />}
        </CustomDetailsCard>
}
